import React, { createContext, useState, useCallback, useEffect } from 'react';
import axiosClient, { setTokens, clearTokens } from '../utils/axiosClient';

export const AuthContext = createContext();

// The auth-service only ever nests permissions under `role.permissions`
// (as populated Permission docs, i.e. { key, description, category }).
// PermissionGate checks a flat `user.permissions` array of key strings,
// so every response needs that array hoisted onto the user object itself.
const normalizeUser = (rawUser) => {
  if (!rawUser) return rawUser;
  const rolePermissions = rawUser.role?.permissions || [];
  return {
    ...rawUser,
    permissions: rolePermissions.map((p) => (typeof p === 'string' ? p : p.key)),
  };
};

export const AuthProvider = ({ children, authServiceUrl = 'http://localhost:4000' }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        try {
          const response = await axiosClient.get(`${authServiceUrl}/auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setUser(normalizeUser(response.data));
        } catch (error) {
          console.error('Failed to fetch user:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [authServiceUrl]);

  // Store a minted session. Called from three places now — plain login, the
  // second factor, and completing a forced enrolment — so the "which of these
  // four writes did that path forget?" question has one answer.
  const applySession = useCallback((data) => {
    setTokens(data.accessToken, data.refreshToken);
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setUser(normalizeUser(data.user));
    return data;
  }, []);

  // A password is no longer necessarily a session. The server answers with one
  // of four shapes, and storing tokens unconditionally would write `undefined`
  // into localStorage on the three that carry none:
  //   { accessToken, … }                            — signed in
  //   { organizationChoiceRequired, organizations }  — this address opens
  //                                                   several firms; which one?
  //   { mfaRequired, mfaToken }                      — has an authenticator
  //   { mfaSetupRequired, mfaToken }                 — role requires one
  //
  // ⚠️ The firm choice is answered by calling this AGAIN with `accountId`, not
  // by a challenge token — the server re-checks the password, which is the only
  // thing that grants anything. So the caller must keep the password until the
  // choice is made.
  const login = useCallback(async (email, password, { accountId, organizationId } = {}) => {
    try {
      const response = await axiosClient.post(
        `${authServiceUrl}/auth/login`,
        { email, password, accountId, organizationId },
        { withCredentials: true }
      );
      if (!response.data.accessToken) return response.data;
      return applySession(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }, [authServiceUrl, applySession]);

  // The other firms this address opens. Used by the firm switcher; returns
  // `{ email, accounts: [{ accountId, organizationId, name, roleName, isCurrent }] }`.
  const listMyOrganizations = useCallback(async () => {
    const response = await axiosClient.get(`${authServiceUrl}/auth/my-organizations`);
    return response.data;
  }, [authServiceUrl]);

  // Step 2: exchange the challenge for a session, with a code from the
  // authenticator app or one of the recovery codes.
  const completeMfaLogin = useCallback(async ({ mfaToken, code }) => {
    try {
      const response = await axiosClient.post(
        `${authServiceUrl}/auth/login/totp`,
        { mfaToken, code },
        { withCredentials: true }
      );
      return applySession(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }, [authServiceUrl, applySession]);

  // Enrolment. Both calls take an OPTIONAL mfaToken: someone already signed in
  // is identified by their access token, someone held at the login screen by
  // the enrol challenge. The server works out which it got.
  const startTotpSetup = useCallback(async ({ mfaToken } = {}) => {
    try {
      const response = await axiosClient.post(
        `${authServiceUrl}/auth/totp/setup`,
        mfaToken ? { mfaToken } : {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }, [authServiceUrl]);

  const confirmTotpSetup = useCallback(async ({ code, mfaToken } = {}) => {
    try {
      const response = await axiosClient.post(
        `${authServiceUrl}/auth/totp/confirm`,
        mfaToken ? { code, mfaToken } : { code },
        { withCredentials: true }
      );
      // The forced path finishes the sign-in in the same call — both factors
      // have just been proved, so there is nothing left to ask for.
      if (response.data.accessToken) applySession(response.data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }, [authServiceUrl, applySession]);

  const register = useCallback(async (userData) => {
    try {
      const response = await axiosClient.post(`${authServiceUrl}/auth/register`, userData, { withCredentials: true });
      setTokens(response.data.accessToken, response.data.refreshToken);
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      setUser(normalizeUser(response.data.user));
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }, [authServiceUrl]);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      await axiosClient.post(`${authServiceUrl}/auth/logout`, { refreshToken }, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearTokens();
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    }
  }, [authServiceUrl]);

  const value = {
    user,
    loading,
    login,
    listMyOrganizations,
    completeMfaLogin,
    startTotpSetup,
    confirmTotpSetup,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
